import React, { useMemo, useState, useEffect } from 'react';
import { useDashboardData } from '@/hooks/useDashboardData';
import { DataService } from '@/services/dataService';
import type { TreatmentRecord } from '@/types/treatment';
import type { Reservation } from '@/types/reservation';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Users,
  Clock,
  Tag,
  Info,
  Trophy,
  User,
  Lightbulb
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ServiceRanking {
  rank: number;
  name: string;
  revenue: number;
  count: number;
}

interface StaffPerformance {
  name: string;
  revenue: number;
  customers: number;
  hourlyProductivity: number;
  repeatRate: number;
  avatar: string;
}

interface CustomerMetrics {
  newCustomers: number;
  returningCustomers: number;
  repeatRate: number;
  averageInterval: number;
  totalCustomers: number;
}

interface MonthlyAnalysisProps {
  className?: string;
}

export const MonthlyAnalysis: React.FC<MonthlyAnalysisProps> = ({ className }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('1month');
  const [staffSelectedPeriod, setStaffSelectedPeriod] = useState<string>('1month');

  // DataServiceとuseDashboardDataの統合
  const { metrics, loading: dashboardLoading, error: dashboardError } = useDashboardData();
  const [treatments, setTreatments] = useState<TreatmentRecord[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);

  // データ取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        const [treatmentsData, reservationsData] = await Promise.all([
          DataService.getTreatments(),
          DataService.getReservations(),
        ]);
        setTreatments(treatmentsData);
        setReservations(reservationsData);
        setDataError(null);
      } catch (err) {
        setDataError(err instanceof Error ? err.message : 'データ取得エラー');
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, []);

  const loading = dashboardLoading || dataLoading;
  const error = dashboardError || dataError;

  // 期間フィルタリング関数
  const getDateRangeForPeriod = (period: string): Date => {
    const now = new Date();
    const rangeDate = new Date();
    
    switch (period) {
      case '3month':
        rangeDate.setMonth(now.getMonth() - 3);
        break;
      case '6month':
        rangeDate.setMonth(now.getMonth() - 6);
        break;
      case '1month':
      default:
        rangeDate.setMonth(now.getMonth() - 1);
        break;
    }
    
    return rangeDate;
  };

  // サービス別売上ランキング計算
  const serviceRanking: ServiceRanking[] = useMemo(() => {
    if (!treatments || treatments.length === 0) return [];
    
    const startDate = getDateRangeForPeriod(selectedPeriod);
    
    // 期間内の施術をフィルター
    const filteredTreatments = treatments.filter(treatment => {
      const treatmentDate = new Date(treatment.date);
      return treatmentDate >= startDate;
    });
    
    // サービス名ごとに集計
    const serviceMap = new Map<string, { revenue: number; count: number }>();
    
    filteredTreatments.forEach(treatment => {
      const serviceName = treatment.treatmentMenu || '不明';
      const revenue = treatment.totalAmount || 0;
      
      const existing = serviceMap.get(serviceName);
      if (existing) {
        existing.revenue += revenue;
        existing.count += 1;
      } else {
        serviceMap.set(serviceName, { revenue, count: 1 });
      }
    });
    
    // 配列に変換して売上順にソート
    const ranking = Array.from(serviceMap.entries())
      .map(([name, data]) => ({
        rank: 0, // 後で設定
        name,
        revenue: data.revenue,
        count: data.count,
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5); // Top 5
    
    // ランク番号を付与
    ranking.forEach((item, index) => {
      item.rank = index + 1;
    });
    
    return ranking;
  }, [treatments, selectedPeriod]);

  // スタッフパフォーマンス計算
  const staffPerformance: StaffPerformance[] = useMemo(() => {
    if (!treatments || treatments.length === 0 || !reservations || reservations.length === 0) {
      return [];
    }
    
    const startDate = getDateRangeForPeriod(staffSelectedPeriod);
    
    // 期間内の施術とreservationをフィルター
    const filteredTreatments = treatments.filter(treatment => {
      const treatmentDate = new Date(treatment.date);
      return treatmentDate >= startDate;
    });
    
    const filteredReservations = reservations.filter(reservation => {
      const reservationDate = new Date(reservation.date);
      return reservationDate >= startDate && reservation.status === 'completed';
    });
    
    // スタッフ名ごとに集計
    const staffMap = new Map<string, {
      revenue: number;
      customers: Set<string>;
      totalCustomers: number;
      repeatCustomers: number;
    }>();
    
    filteredTreatments.forEach(treatment => {
      const staffName = treatment.staffName || '不明';
      const revenue = treatment.totalAmount || 0;
      const customerId = treatment.customerId.toString() || '';
      
      const existing = staffMap.get(staffName);
      if (existing) {
        existing.revenue += revenue;
        existing.customers.add(customerId);
      } else {
        staffMap.set(staffName, {
          revenue,
          customers: new Set([customerId]),
          totalCustomers: 0,
          repeatCustomers: 0,
        });
      }
    });
    
    // 顧客数とリピート率を計算
    filteredReservations.forEach(reservation => {
      const staffName = reservation.staffName || '不明';
      const customerId = reservation.customerId.toString() || '';
      
      const staffData = staffMap.get(staffName);
      if (staffData) {
        staffData.totalCustomers += 1;
        
        // リピート顧客判定（同じ顧客IDが複数回）
        const customerReservations = filteredReservations.filter(
          r => r.customerId.toString() === customerId && r.staffName === staffName
        );
        if (customerReservations.length > 1) {
          staffData.repeatCustomers += 1;
        }
      }
    });
    
    // 配列に変換して売上順にソート
    const performance = Array.from(staffMap.entries())
      .map(([name, data]) => {
        const customersCount = data.customers.size;
        const hourlyProductivity = customersCount > 0 ? 
          (customersCount / 160).toFixed(1) : '0.0'; // 月160時間想定
        const repeatRate = data.totalCustomers > 0 ? 
          ((data.repeatCustomers / data.totalCustomers) * 100).toFixed(1) : '0.0';
        
        return {
          name,
          revenue: data.revenue,
          customers: customersCount,
          hourlyProductivity: parseFloat(hourlyProductivity),
          repeatRate: parseFloat(repeatRate),
          avatar: name.charAt(0), // 最初の1文字
        };
      })
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5); // Top 5
    
    return performance;
  }, [treatments, reservations, staffSelectedPeriod]);

  // 顧客メトリクス計算（useDashboardDataのmetricsを活用）
  const customerMetrics: CustomerMetrics = useMemo(() => {
    if (!reservations || reservations.length === 0) {
      return {
        newCustomers: 0,
        returningCustomers: 0,
        repeatRate: metrics.repeatRate || 0,
        averageInterval: 28, // デフォルト値
        totalCustomers: metrics.monthlyCustomers || 0,
      };
    }
    
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // 今月の完了した予約
    const thisMonthReservations = reservations.filter(reservation => {
      const reservationDate = new Date(reservation.date);
      return reservationDate.getMonth() === currentMonth && 
             reservationDate.getFullYear() === currentYear &&
             reservation.status === 'completed';
    });
    
    // 顧客IDの集計
    const customerIds = new Set(thisMonthReservations.map(r => r.customerId));
    const totalCustomers = customerIds.size;
    
    // 新規顧客判定（今月が初回来店）
    const customerFirstVisit = new Map<number, Date>();
    reservations
      .filter(r => r.status === 'completed')
      .forEach(r => {
        const customerId = r.customerId;
        const visitDate = new Date(r.date);
        
        if (!customerFirstVisit.has(customerId) || 
            visitDate < customerFirstVisit.get(customerId)!) {
          customerFirstVisit.set(customerId, visitDate);
        }
      });
    
    let newCustomers = 0;
    customerIds.forEach(customerId => {
      const firstVisit = customerFirstVisit.get(customerId);
      if (firstVisit) {
        if (firstVisit.getMonth() === currentMonth && 
            firstVisit.getFullYear() === currentYear) {
          newCustomers++;
        }
      }
    });
    
    const returningCustomers = totalCustomers - newCustomers;
    const repeatRate = totalCustomers > 0 ? 
      ((returningCustomers / totalCustomers) * 100) : 
      metrics.repeatRate;
    
    // 平均来店間隔計算（簡易実装）
    const averageInterval = 28; // 暫定値（後で実データ計算に置き換え可能）
    
    return {
      newCustomers,
      returningCustomers,
      repeatRate: Math.round(repeatRate * 10) / 10,
      averageInterval,
      totalCustomers,
    };
  }, [reservations, metrics]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(value);
  };

  const getRankingColor = (rank: number) => {
    switch (rank) {
      case 1: return 'bg-amber-500';
      case 2: return 'bg-gray-400';
      case 3: return 'bg-orange-400';
      default: return 'bg-gray-300';
    }
  };

  /**
   * 動的Insight Message生成関数
   * リピート率と新規顧客率に基づいて、適切なアドバイスメッセージを生成
   */
  const generateInsightMessage = (metrics: CustomerMetrics): string => {
    const repeatRate = metrics.repeatRate;
    const newCustomerRate = metrics.totalCustomers > 0
      ? (metrics.newCustomers / metrics.totalCustomers) * 100
      : 0;

    // 極めて高いリピート率 (>=90%)
    if (repeatRate >= 90) {
      if (newCustomerRate < 20) {
        return `リピート率${repeatRate}%は極めて優秀です。新規顧客獲得施策により、さらなる成長が見込めます。`;
      } else {
        return `リピート率${repeatRate}%は業界トップレベルです。現在の新規獲得ペースを維持し、既存顧客との関係性を強化しましょう。`;
      }
    }

    // 良好なリピート率 (70-89%)
    if (repeatRate >= 70) {
      if (newCustomerRate < 25) {
        return `リピート率${repeatRate}%は良好です。新規顧客の獲得強化により、売上拡大のチャンスがあります。`;
      } else {
        return `リピート率${repeatRate}%で新規獲得も順調です。顧客満足度の維持とリピーター育成に注力しましょう。`;
      }
    }

    // 平均的なリピート率 (50-69%)
    if (repeatRate >= 50) {
      return `リピート率${repeatRate}%は平均的です。顧客満足度向上施策（フォローアップ、特典プログラム等）を検討しましょう。`;
    }

    // 低いリピート率 (<50%)
    return `リピート率${repeatRate}%は改善の余地があります。顧客体験の見直しや、リピート促進施策の実施を推奨します。`;
  };

  // Loading状態
  if (loading) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center h-[500px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-muted-foreground">データを読み込み中...</span>
        </div>
      </div>
    );
  }

  // Error状態
  if (error) {
    return (
      <div className={className}>
        <Alert variant="destructive">
          <AlertDescription>
            データの読み込みに失敗しました: {error}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // データが空の場合
  if (serviceRanking.length === 0 && staffPerformance.length === 0) {
    return (
      <div className={className}>
        <Alert>
          <AlertDescription>
            表示するデータがありません。予約や施術記録を追加してください。
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-12">
        {/* サービス別ランキング */}
        <div className="lg:col-span-4">
          <Card className="min-h-[500px]">
            <CardHeader>
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base font-semibold whitespace-nowrap">
                    <Trophy className="w-4 h-4 mr-2 inline-block" />サービス別売上ランキング
                  </CardTitle>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[200px]">
                        <p className="text-xs">選択期間内の完了予約から集計したサービス別売上です</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <ToggleGroup
                  type="single"
                  value={selectedPeriod}
                  onValueChange={(value) => value && setSelectedPeriod(value)}
                  className="h-7"
                >
                  <ToggleGroupItem value="1month" className="px-3 text-xs">
                    1ヶ月
                  </ToggleGroupItem>
                  <ToggleGroupItem value="3month" className="px-3 text-xs">
                    3ヶ月
                  </ToggleGroupItem>
                  <ToggleGroupItem value="6month" className="px-3 text-xs">
                    6ヶ月
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {serviceRanking.map((service) => (
                  <div key={service.rank} className="flex items-center gap-4">
                    <div className={`w-8 h-8 ${getRankingColor(service.rank)} rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                      {service.rank}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">{service.name}</span>
                        <span className="text-sm font-bold text-primary">
                          {formatCurrency(service.revenue)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {service.count}回
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* スタッフパフォーマンス */}
        <div className="lg:col-span-8">
          <Card className="min-h-[500px]">
            <CardHeader>
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base font-semibold whitespace-nowrap">
                    <Users className="w-4 h-4 mr-2 inline-block" />スタッフパフォーマンス
                  </CardTitle>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[250px]">
                        <p className="text-xs">選択期間内のスタッフ別売上・客数・時間生産性・リピート率です</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <ToggleGroup
                  type="single"
                  value={staffSelectedPeriod}
                  onValueChange={(value) => value && setStaffSelectedPeriod(value)}
                  className="h-7"
                >
                  <ToggleGroupItem value="1month" className="px-3 text-xs">
                    1ヶ月
                  </ToggleGroupItem>
                  <ToggleGroupItem value="3month" className="px-3 text-xs">
                    3ヶ月
                  </ToggleGroupItem>
                  <ToggleGroupItem value="6month" className="px-3 text-xs">
                    6ヶ月
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>スタッフ</TableHead>
                    <TableHead className="text-right">売上</TableHead>
                    <TableHead className="text-right">客数</TableHead>
                    <TableHead className="text-right">時間生産性</TableHead>
                    <TableHead className="text-right">リピート率</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffPerformance.map((staff) => (
                    <TableRow key={staff.name}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                              {staff.avatar}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">{staff.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-bold">
                          {formatCurrency(staff.revenue)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{staff.customers}名</TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-medium">
                          {staff.hourlyProductivity}人/h
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div>
                          <p className="text-sm font-medium mb-1">
                            {staff.repeatRate}%
                          </p>
                          <Progress 
                            value={staff.repeatRate} 
                            className="h-1 w-16"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* 顧客分析 */}
        <div className="lg:col-span-12 mb-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                <User className="w-4 h-4 mr-2 inline-block" />顧客分析
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-4 mb-6">
                <div className="text-center p-4 bg-muted/30 rounded-lg border hover:bg-muted/50 transition-colors">
                  <Users className="w-10 h-10 text-primary mx-auto mb-2" />
                  <p className="text-3xl font-bold text-foreground">
                    {customerMetrics.totalCustomers}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    総来客数
                  </p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg border hover:bg-muted/50 transition-colors">
                  <Tag className="w-10 h-10 text-primary mx-auto mb-2" />
                  <p className="text-3xl font-bold text-foreground">
                    {customerMetrics.repeatRate}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    リピート率
                  </p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg border hover:bg-muted/50 transition-colors">
                  <Users className="w-10 h-10 text-primary mx-auto mb-2" />
                  <p className="text-3xl font-bold text-foreground">
                    {customerMetrics.newCustomers}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    新規顧客（今月）
                  </p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg border hover:bg-muted/50 transition-colors">
                  <Clock className="w-10 h-10 text-primary mx-auto mb-2" />
                  <p className="text-3xl font-bold text-foreground">
                    {customerMetrics.averageInterval}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    平均来店間隔(日)
                  </p>
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      新規顧客（今月）
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {customerMetrics.newCustomers}名 ({((customerMetrics.newCustomers / customerMetrics.totalCustomers) * 100).toFixed(1)}%)
                    </p>
                  </div>
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    NEW
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      リピーター（今月）
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {customerMetrics.returningCustomers}名 ({((customerMetrics.returningCustomers / customerMetrics.totalCustomers) * 100).toFixed(1)}%)
                    </p>
                  </div>
                  <Badge variant="outline" className="text-primary border-primary">
                    RETURN
                  </Badge>
                </div>
              </div>

              <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    {generateInsightMessage(customerMetrics)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};